import { readJson, writeJson } from './storage'

/**
 * A LOCAL copy of the questionnaire, written on every keystroke and tap.
 *
 * Until now the only copy of an in-progress answer lived on the server, saved
 * once per question and NOT awaited:
 *
 *     persist(next, false).catch((e) => console.warn(...))
 *
 * So a single failed request — the backend restarting, a 500 caused by some
 * earlier bad answer still sitting in the draft row, a dropped connection on a
 * phone — lost that answer and every answer after it, with nothing on screen
 * to say so. That is why answers "were not cached": there was no cache.
 *
 * This is that cache. It is deliberately dumb: whole-object write on every
 * change, synchronous, no network. Refreshing the page now restores what you
 * typed even if the backend never heard about it.
 *
 * Scoped per profile so two accounts on one browser cannot read each other's
 * answers, and stamped so a forgotten draft cannot haunt someone months later.
 */

const KEY = (profileId: string) => `tc_onboarding_draft:${profileId}`

/** Older than this and we assume the server copy is the real one. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

interface Draft {
  answers: Record<string, unknown>
  savedAt: number
}

export function readDraft(profileId?: string | null): Record<string, unknown> {
  if (!profileId) return {}
  const draft = readJson<Draft | null>(KEY(profileId), null)
  if (!draft || typeof draft.savedAt !== 'number') return {}
  if (Date.now() - draft.savedAt > MAX_AGE_MS) {
    clearDraft(profileId)
    return {}
  }
  return draft.answers || {}
}

export function writeDraft(profileId: string | null | undefined, answers: Record<string, unknown>) {
  if (!profileId) return
  writeJson(KEY(profileId), { answers, savedAt: Date.now() } satisfies Draft)
}

/** Called once the questionnaire is submitted — the server copy is now truth. */
export function clearDraft(profileId?: string | null) {
  if (!profileId) return
  try { localStorage.removeItem(KEY(profileId)) } catch { /* private mode */ }
}
